import { getWebOutbox } from "@client/iphone/lib/videoSync/createVideoSync";
import {
  createSyncFeature,
  type StorageLike,
  type SyncFeature,
} from "@client/sync";
import { trpcVanilla } from "@client/trpc";
import { type PresenceSegment } from "./presenceSegments";

export interface PresencePayload {
  segmentId: string;
  startedAt: string;
  endedAt: string;
  inputMinutes: number[];
  pointerMinutes: number[];
  /** `Capacitor.isNativePlatform()` at the time the segment was built. */
  native: boolean;
  /** iPad (native shell or iPad browser) / iPhone shell; absent elsewhere. */
  device?: "ipad" | "iphone";
  buildId: string;
  /** Page family and round reference the segment was on; absent when the
   *  builder had none. */
  page?: string;
  roundRef?: string;
  /** The segment this one continues (rollover / navigation), if any. */
  continuesSegmentId?: string;
}

export interface PresenceClientLabels {
  native: boolean;
  device?: "ipad" | "iphone";
  buildId: string;
}

export function toPresencePayload(
  segment: PresenceSegment,
  labels: PresenceClientLabels,
): PresencePayload {
  return {
    segmentId: segment.segmentId,
    startedAt: new Date(segment.startedAtMs).toISOString(),
    endedAt: new Date(segment.endedAtMs).toISOString(),
    inputMinutes: segment.inputMinutes,
    pointerMinutes: segment.pointerMinutes,
    native: labels.native,
    device: labels.device,
    buildId: labels.buildId,
    ...(segment.page == null ? {} : { page: segment.page }),
    ...(segment.roundRef == null ? {} : { roundRef: segment.roundRef }),
    ...(segment.continuesSegmentId == null
      ? {}
      : { continuesSegmentId: segment.continuesSegmentId }),
  };
}

/**
 * Durable outbox for client presence segments, a sibling of
 * `createWatchSegmentSync` with the same storage split: native persists to
 * localStorage (presence accrued offline survives a force-quit and flushes on
 * reconnect), web uses the per-user in-memory outbox. The storage key is
 * per-user AND distinct from every other sync feature's key — two workers
 * over one queue double-send.
 *
 * Coalescing is by SEGMENT id: while a segment grows, each checkpoint
 * replaces its queued entry in place (the queue never holds two versions of
 * one segment), while a NEW segment appends. The server upserts by id behind
 * an `endedAt`-grows guard, so replay/resend order can't shorten a row.
 */
export function createPresenceSync(args: {
  userId: string;
  isNative: boolean;
  // Test seams; production uses the real defaults.
  storage?: StorageLike;
  isOnline?: () => boolean;
  // See createSyncFeature — commit gate for sends racing an identity flip.
  shouldCommit?: () => boolean;
}): SyncFeature<PresencePayload> {
  return createSyncFeature<PresencePayload, { applied: boolean }>({
    storageKey: `presenceSync.segments.v1.${args.userId}`,
    storage:
      args.storage ?? (args.isNative ? undefined : getWebOutbox(args.userId)),
    coalesce: (existing, incoming) =>
      existing.segmentId === incoming.segmentId ? incoming : null,
    send: (payload) => trpcVanilla.recordPresenceSegment.mutate(payload),
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}
